import { type TypeTableColumn } from '@thefarmersfront/kitchen';

import { BadgeCell, TextButtonCell } from '~/components/common';

import { formatDate, formatNumber } from '~/utils';

import { DATE_FORMAT, STOCK_RELEASE_REQUEST_STATUS } from '~/constants';

import type { TypeStockReleaseInfo } from '~/types/stock.types';

type TypeStockReleaseStatusKey = keyof typeof STOCK_RELEASE_REQUEST_STATUS;

const formatCount = (value: unknown): string => (typeof value === 'number' && value ? formatNumber(value) : '-');

/**
 * [재고관리 > 반출 요청 관리] 반출 요청 조회 테이블
 */
const getStockReleaseColumnDefs = ({ onClickViewDetail }: { onClickViewDetail: (id: number) => void }) => {
  const columnDefs: TypeTableColumn<TypeStockReleaseInfo>[] = [
    {
      id: 'no',
      header: 'No',
      cell: ({ row }) => row.index + 1,
      size: 72,
      meta: { selection: true },
    },
    {
      accessorKey: 'id',
      header: '반출요청번호',
      size: 180,
    },
    {
      accessorKey: 'statusCode',
      header: '반출 상태',
      size: 100,
      minSize: 100,
      cell: ({ getValue }) => {
        const code = getValue<TypeStockReleaseStatusKey>();
        const status = STOCK_RELEASE_REQUEST_STATUS[code];
        const colorMap = { [code]: { label: status?.text ?? '', color: status?.color } };
        return <BadgeCell value={code} colorMap={colorMap} variant="light" status />;
      },
    },
    {
      accessorKey: 'clusterCodeObject.name',
      header: '반출 출발지 센터',
      size: 120,
    },
    {
      accessorKey: 'methodCodeObject.name',
      header: '반출 방식',
      size: 80,
    },
    {
      accessorKey: 'expectedDate',
      header: '반출 희망일',
      size: 100,
      cell: ({ getValue }) => formatDate(getValue<TypeStockReleaseInfo['expectedDate']>(), DATE_FORMAT.DATE) ?? '-',
    },
    {
      accessorKey: 'skuCount',
      header: 'SKU 수',
      size: 80,
      cell: ({ getValue }) => formatCount(getValue()),
      meta: { cellStyle: { textAlign: 'right' } },
    },
    {
      accessorKey: 'totalRequestQuantity',
      header: '총 수량',
      size: 80,
      cell: ({ getValue }) => formatCount(getValue()),
      meta: { cellStyle: { textAlign: 'right' } },
    },
    {
      accessorKey: 'createdAt',
      header: '요청등록일시',
      size: 160,
      cell: ({ getValue }) => formatDate(getValue<TypeStockReleaseInfo['createdAt']>(), DATE_FORMAT.DATE_TIME) ?? '-',
    },
    {
      id: 'viewDetail',
      header: '',
      minSize: 80,
      cell: ({ row }) => (
        <TextButtonCell
          showDashIfNoValue={false}
          onClick={() => {
            if (row.original.id) onClickViewDetail(row.original.id);
          }}
        >
          상세보기
        </TextButtonCell>
      ),
      meta: { flex: true },
    },
  ];

  return { columnDefs };
};

export default getStockReleaseColumnDefs;
